import {BaseColonistStoreProduct, PaymentMethod} from '../../../../shared/store/BaseColonistStoreProduct'
import {ColonistCoinProduct} from '../../../../shared/store/ColonistCoinProduct'
import {ColonistMembershipProduct} from '../../../../shared/store/ColonistMembershipProduct'
import {getParameterByName} from '../ui_utils/ClientUtils'
import {UICheckoutAjaxEndPoints} from './UICheckoutAjaxEndPoints'
import {getLobbyImagePath, logError} from '../ui_utils/Utils'
import {setElementInnerHTML, setElementOnClick} from '../ui_utils/DomUtils'
import {TranslationObject} from '@colonist/i18n'
import {ColonistOneTimePurchaseProduct} from '../../../../shared/store/ColonistOneTimePurchaseProduct'

export class UICheckoutPageController {
    private readonly paynowButton: HTMLLinkElement
    private readonly itemImage: HTMLImageElement
    private readonly itemPrice: HTMLElement
    private readonly itemName: HTMLSpanElement
    private readonly radioButtons: NodeListOf<HTMLElement>
    private readonly product: BaseColonistStoreProduct

    constructor() {
        this.paynowButton = document.getElementById('checkout_paynow_button') as HTMLLinkElement
        this.itemImage = document.getElementById('checkout_item_image') as HTMLImageElement
        this.itemPrice = document.getElementById('checkout_item_price') as HTMLElement
        this.itemName = document.getElementById('checkout_item_name') as HTMLSpanElement
        this.radioButtons = document.getElementsByName('payment_method')
        const storeProduct = UICheckoutPageController.getStoreProduct()
        if(storeProduct == null) {
            this.deactivatePaynowButton()
            return
        }
        this.product = storeProduct

        this.hideNonStripePayments()
        this.activatePaynowButton()
        this.generateCheckoutItem()
        this.connectPaymentForm()
    }

    private hideNonStripePayments() {
        if(!this.isMembershipProduct()) return

        const labelPaypal = document.getElementById('label_paypal') as HTMLElement
        labelPaypal.style.display = 'none'
        const labelAmazonPay = document.getElementById('label_amazon_pay') as HTMLElement
        labelAmazonPay.style.display = 'none'
        const labelBitpay = document.getElementById('label_bitpay') as HTMLElement
        labelBitpay.style.display = 'none'
        const labelOtherMethods = document.getElementById('label_other_methods') as HTMLElement
        labelOtherMethods.style.display = 'none'
    }

    private deactivatePaynowButton() {
        this.paynowButton.classList.add('inactive')
        setElementOnClick(this.paynowButton, () => undefined)
    }

    private activatePaynowButton() {
        this.paynowButton.classList.remove('inactive')
        setElementOnClick(this.paynowButton, () => this.clickedPaynow())
    }

    private static getStoreProduct(): BaseColonistStoreProduct | undefined {
        const coinProductId = getParameterByName('product')
        if(coinProductId != null) {
            const coinProductIdNumber = Number(coinProductId)
            const colonistCoinProduct = ColonistCoinProduct.getPurchaseProduct(coinProductIdNumber)
            return colonistCoinProduct
        }

        const membershipProductId = getParameterByName('membership')
        if(membershipProductId != null) {
            const membershipProductIdNumber = Number(membershipProductId)
            const membershipProduct = ColonistMembershipProduct.getMembershipProduct(membershipProductIdNumber)
            return membershipProduct
        }

        const oneTimePurchaseProductId = getParameterByName('one-time-purchase')
        if(oneTimePurchaseProductId != null) {
            const oneTimePurchaseProductIdNumber = Number(oneTimePurchaseProductId)
            const oneTimePurchaseProduct = ColonistOneTimePurchaseProduct.getOneTimePurchaseProduct(oneTimePurchaseProductIdNumber)
            if(oneTimePurchaseProduct.isActive() == false) return undefined
            return oneTimePurchaseProduct
        }

        return undefined
    }

    private generateCheckoutItem() {
        if(this.isMembershipProduct()) {
            this.generateMembershipCheckoutText()
            return
        }
        this.generateCoinCheckoutText()
    }

    private generateMembershipCheckoutText() {
        const productMembership = this.product as ColonistMembershipProduct
        if(productMembership.billedAnnually) {
            setElementInnerHTML(this.itemPrice, {key: 'strings:storePage.sections.membership.billing.yearlyPayment', options: {amount: productMembership.getAnnualPrice()}})
        } else {
            setElementInnerHTML(this.itemPrice, {key: 'strings:colonistCommon.utils.#buffer', options: {value: `$` + this.product.getDefaultPrice()}})
        }
        setElementInnerHTML(this.itemName, this.product.getProductName())
        this.generateMembershipRenewalCheckoutText()
        this.itemImage.src = getLobbyImagePath(this.product.lobbyImage)
    }

    private generateCoinCheckoutText() {
        setElementInnerHTML(this.itemPrice, {key: 'strings:colonistCommon.utils.#buffer', options: {value: this.product.getDefaultPrice()}})
        setElementInnerHTML(this.itemName, this.product.getProductName())
        this.itemImage.src = getLobbyImagePath(this.product.lobbyImage)
    }

    private generateMembershipRenewalCheckoutText() {
        const membershipRenewalInfoContainer = document.getElementById('membership-renewal-info') as HTMLDivElement
        const renewalDateString = this.calculateMembershipRenewalDate()
        const text: TranslationObject = {key: 'strings:storePage.checkout.membershipRenewalInfo', options: {date: renewalDateString}}
        setElementInnerHTML(membershipRenewalInfoContainer, text)
    }

    private calculateMembershipRenewalDate(): string {
        const renewalDate = new Date()

        const productMembership = this.product as ColonistMembershipProduct
        if(productMembership.billedAnnually) {
            renewalDate.setFullYear(renewalDate.getFullYear() + 1)
        } else {
            renewalDate.setMonth(renewalDate.getMonth() + 1)
        }

        return renewalDate.toLocaleDateString()
    }

    private connectPaymentForm() {
        for(const button of this.radioButtons) {
            setElementOnClick(button, () => this.activatePaynowButton())
        }
    }

    private getSelectedPaymentMethod(): PaymentMethod {
        for(const button of this.radioButtons) {
            if(!(button as HTMLInputElement).checked) continue
            switch(button.id) {
                case 'credit_debit':
                case 'google_pay':
                case 'apple_pay': return PaymentMethod.Stripe
                case 'paypal':
                case 'amazon_pay':
                case 'bitpay':
                case 'other_methods': return PaymentMethod.Xsolla
            }
        }
        // eslint-disable-next-line no-console
        logError('getSelectedPaymentMethod', ['Error wtf'])
        return PaymentMethod.Stripe
    }

    private isMembershipProduct(): boolean {
        const isMembershipProduct = this.product instanceof ColonistMembershipProduct
        return isMembershipProduct
    }

    private clickedPaynow() {
        const selectedPaymentMethod = this.getSelectedPaymentMethod()

        if(selectedPaymentMethod == PaymentMethod.Stripe) {
            void UICheckoutAjaxEndPoints.executeStripeColonistProductPurchaseSession(this.product.storeProductType, this.product.type, this.product.itemQuantity)
        }
        if(selectedPaymentMethod == PaymentMethod.Xsolla) {
            void UICheckoutAjaxEndPoints.executeXsollaColonistCoinPurchaseSession(this.product.storeProductType, this.product.type)
        }
    }
}
